import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  useSearchParams,
  Link,
  useNavigate,
  Outlet,
  useParams,
  useLocation,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { SiteContext } from '../../contexts/SiteContext';
import CreateNewItemModal from '../modals/CreateNewItemModal';
import DoubleInputForm from '../forms/DoubleInputForm';

export default function EditWebsitePage() {
  const { t } = useTranslation();
  const { website: currentSite } = useContext(SiteContext);
  const websiteId = currentSite?._id;
  const navigate = useNavigate();
  const location = useLocation();
  const { sectionSlug: routeSectionSlug } = useParams();

  const [sp, setSp] = useSearchParams();
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const selectedSectionId = sp.get('sc');

  // Are we on a nested editor route? (…/edit/:sectionSlug/item[/...])
  const isInItemRoute = /\/edit\/[^/]+\/item(\/|$)/.test(location.pathname);

  useEffect(() => {
    if (!websiteId) return;
    (async () => {
      try {
        setLoadingSections(true);
        setError('');
        const { data } = await api.get(`/site-contents/by-website/${websiteId}`);
        const list = Array.isArray(data) ? data : [];
        setSections(list);

        // If ?sc is missing, sync from route slug; else default to first.
        if (!sp.get('sc')) {
          const bySlug = routeSectionSlug && list.find(s => s.slug === routeSectionSlug);
          const target = bySlug?._id ?? list[0]?._id;
          if (target) {
            const next = new URLSearchParams(sp);
            next.set('sc', target);
            setSp(next, { replace: true });
          }
        }
      } catch {
        setError(t('errors.failedToLoadSections'));
      } finally {
        setLoadingSections(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId, routeSectionSlug]);

  const selectedSection = useMemo(() => {
    const byId = sections.find(s => String(s._id) === String(selectedSectionId));
    if (byId) return byId;
    if (routeSectionSlug) return sections.find(s => s.slug === routeSectionSlug);
    return null;
  }, [sections, selectedSectionId, routeSectionSlug]);

  // Auto-jump into editor when section type === 'Editor'
  useEffect(() => {
    if (!selectedSection) return;
    if ((selectedSection.type || '').toLowerCase() === 'editor') {
      // Only redirect if we're not already on this editor route
      if (!isInItemRoute || routeSectionSlug !== selectedSection.slug) {
        navigate(`./${selectedSection.slug}/item`, { replace: true });
      }
    }
  }, [selectedSection, routeSectionSlug, isInItemRoute, navigate]);

  // Expose "append" + "reload" for list table
  const tableRef = useRef();

  // When coming back from an item route to the section root, reload table
  const wasInItemRouteRef = useRef(false);
  useEffect(() => {
    const wasInItem = wasInItemRouteRef.current;
    if (wasInItem && !isInItemRoute) {
      tableRef.current?.reload?.();
    }
    wasInItemRouteRef.current = isInItemRoute;
  }, [isInItemRoute]);

  return (
    <div className="flex gap-6">
      <aside className="w-64 bg-white border rounded shadow p-3 h-fit">
        <h2 className="font-semibold mb-2">{t('editor.sections')}</h2>
        {loadingSections ? (
          <div>{t('general.loading')}</div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : sections.length ? (
          <ul className="space-y-1">
            {sections.map(sc => (
              <li key={sc._id}>
                <button
                  className={`w-full text-left px-2 py-1 rounded ${
                    String(sc._id) === String(selectedSectionId)
                      ? 'bg-gray-200 font-medium'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    const next = new URLSearchParams(sp);
                    next.set('sc', sc._id);
                    setSp(next, { replace: true });

                    // Jump directly to editor pages
                    if ((sc.type || '').toLowerCase() === 'editor') {
                      navigate(`./${sc.slug}/item`, { replace: true });
                    } else if (routeSectionSlug !== sc.slug && isInItemRoute) {
                      // If we are in an item route of another section, move back to section root
                      navigate(`../edit?${next.toString()}`, { replace: true });
                    }
                  }}
                  title={sc.about || sc.name}
                >
                  {sc.name}
                  {typeof sc.itemCount === 'number' ? ` (${sc.itemCount})` : ''}
                  {sc.type ? (
                    <span className="text-xs text-gray-500"> · {sc.type}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-gray-600">{t('editor.noSections')}</div>
        )}
      </aside>

      <main className="flex-1 bg-white border rounded shadow p-4">
        <header className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold">
            {selectedSection
              ? t('editor.editWebsiteWith', { name: selectedSection.name })
              : t('editor.selectSection')}
          </h1>

          {/* Only show "New item" in List/Double modes and when not inside the editor route */}
          {selectedSection &&
            !isInItemRoute &&
            ['list editor', 'list', 'double input form', 'double'].includes(
              (selectedSection.type || '').toLowerCase()
            ) && (
              <button
                className="px-3 py-2 rounded bg-blue-600 text-white"
                onClick={() => setShowNew(true)}
              >
                + {t('editor.newItem')}
              </button>
            )}
        </header>

        {!selectedSection ? (
          <div>{t('editor.leftMenuPrompt')}</div>
        ) : isInItemRoute ? (
          // Any section’s editor view (Outlet renders ContentItemEditorPage)
          <Outlet />
        ) : (selectedSection.type || '').toLowerCase() === 'double input form' ||
          (selectedSection.type || '').toLowerCase() === 'double' ? (
          <DoubleInputForm
            sectionId={selectedSection._id}
            inputName={selectedSection.inputName || t('editor.value')}
            reloadKey={reloadKey}
          />
        ) : (
          // Default: List Editor table
          <ContentItemsTable
            ref={tableRef}
            sectionId={selectedSection._id}
            sectionSlug={selectedSection.slug}
          />
        )}

        {/* New item modal (works for list + double modes) */}
        {selectedSection && (
          <CreateNewItemModal
            open={showNew}
            onClose={() => setShowNew(false)}
            sectionId={selectedSection._id}
            title={t('editor.createIn', { name: selectedSection.name })}
            onCreated={(newItem) => {
              tableRef.current?.appendRow?.(newItem);
              setReloadKey(k => k + 1);
              setShowNew(false);
            }}
          />
        )}
      </main>
    </div>
  );
}

// ===== list table =====
function formatDate(ts) {
  try {
    return new Date(ts || Date.now()).toLocaleString();
  } catch {
    return '';
  }
}

const ContentItemsTable = forwardRef(function ContentItemsTable({ sectionId, sectionSlug }, ref) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busyIds, setBusyIds] = useState(new Set());

  useImperativeHandle(ref, () => ({
    appendRow: (r) => setRows(prev => [r, ...prev]),
    reload: () => fetchItems(),
  }));

  async function fetchItems() {
    try {
      setLoading(true);
      setErr('');
      const { data } = await api.get(`/content-items/by-content/${sectionId}`);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setErr(t('errors.failedToLoadItems'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('editor.confirmDelete'))) return;
    setBusyIds(prev => new Set(prev).add(id));
    try {
      await api.delete(`/content-items/${id}`);
      setRows(prev => prev.filter(r => r._id !== id));
    } catch (e) {
      window.alert(e?.response?.data?.msg || t('errors.deleteFailed'));
    } finally {
      setBusyIds(prev => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  }

  useEffect(() => {
    if (sectionId) fetchItems();
  }, [sectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div>{t('general.loading')}</div>;
  if (err) return <div className="text-red-600">{err}</div>;

  return (
    <div className="overflow-auto">
      <table className="min-w-[600px] w-full border">
        <thead className="bg-gray-100">
          <tr>
            <th className="text-left p-2 border">{t('editor.table.name')}</th>
            <th className="text-left p-2 border">{t('editor.table.link')}</th>
            <th className="text-left p-2 border">{t('editor.table.updated')}</th>
            <th className="text-left p-2 border">{t('editor.table.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map(r => (
            <tr key={r._id} className="hover:bg-gray-50">
              <td className="p-2 border">{r.name}</td>
              <td className="p-2 border">{r.link}</td>
              <td className="p-2 border">{formatDate(r.updatedAt)}</td>
              <td className="p-2 border">
                <Link
                  className="text-blue-700 underline mr-3"
                  to={`./${sectionSlug}/item/${r.link}`}
                >
                  {t('editor.actions.edit')}
                </Link>
                <button
                  className="text-red-700 underline disabled:opacity-50"
                  onClick={() => handleDelete(r._id)}
                  disabled={busyIds.has(r._id)}
                >
                  {busyIds.has(r._id)
                    ? t('editor.actions.removing')
                    : t('editor.actions.delete')}
                </button>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td className="p-4 text-gray-500" colSpan={4}>
                {t('editor.empty.noItems')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
});
